import React, { useState } from 'react';
import { generateQRCodeSVG } from '../../utils/qrCodeGenerator';
import { LuTicket, LuCalendar, LuLink, LuScan, LuTrash2, LuMapPin, LuClock, LuSave, LuCheck, LuPrinter, LuSmartphone, LuGlobe, LuX, LuPlus, LuPencil, LuSettings, LuCircleAlert } from 'react-icons/lu';

export const DEFAULT_TICKETING_CONFIG = {
    enabled: true,
    isLocked: false,
    addOnPrice: '$49/mo',
    licenseKey: 'OMNI-TICKETS-PRO-ACTIVATED',
    licenseTier: 'Event Ticketing & Pass Suite',
    slug: 'events',
    currency: 'USD',
    organizerName: 'OmniVaso Events',
    organizerEmail: 'events@omnivaso.com',
    heroBadge: '🎟️ OFFICIAL EVENT TICKETS',
    showHeroBadge: true,
    heroTitle: 'Upcoming Events & Gatherings',
    heroSubtitle: 'Reserve your spot today. Choose from Free, Sliding Scale, Early Bird, Regular, or VIP passes with instant digital QR tickets or authorized partner checkout.',
    eventbriteToken: '',
    eventbriteOrgId: '',
    events: [
        {
            id: 'evt-sample-1',
            title: 'OmniVaso Community Summit 2026',
            subtitle: 'Creative Tech, Design & Innovation',
            date: '2026-11-15',
            time: '18:00',
            location: 'Grand Main Stage & Live Stream',
            description: 'Join creators, developers, and visionaries for an unforgettable interactive experience.',
            bannerUrl: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80',
            provider: 'internal', // 'internal' | 'eventbrite' | 'ticketmaster' | 'humanitix' | 'custom'
            externalTicketUrl: '',
            eventbriteEventId: '',
            externalButtonText: 'Buy Tickets on Eventbrite',
            tiers: [
                {
                    id: 'tier-earlybird', type: 'early_bird', name: 'Early Bird Special Pass', enabled: true, capacity: 50, price: 18,
                    badgeText: 'EARLY BIRD', badgeBg: '#fef3c7', badgeColor: '#b45309', cardBg: '#fffbeb', cardBorderColor: '#f59e0b', textColor: '#0f172a', buttonText: 'Claim Early Bird Pass', perks: 'Special early registration discount'
                },
                {
                    id: 'tier-free', type: 'free', name: 'Community Free Pass', enabled: true, capacity: 50, price: 0,
                    badgeText: 'FREE', badgeBg: '#dcfce7', badgeColor: '#15803d', cardBg: '#f0fdf4', cardBorderColor: '#22c55e', textColor: '#0f172a', buttonText: 'Claim Free Ticket', perks: 'General entry access'
                },
                {
                    id: 'tier-sliding', type: 'sliding_scale', name: 'Sliding Scale Ticket', enabled: true, capacity: 100, minPrice: 5, maxPrice: 50, suggestedPrice: 20,
                    badgeText: 'SLIDING SCALE', badgeBg: '#fef3c7', badgeColor: '#b45309', cardBg: '#ffffff', cardBorderColor: '#e2e8f0', textColor: '#0f172a', buttonText: 'Choose Your Price', perks: 'Pay what you can support'
                },
                {
                    id: 'tier-regular', type: 'regular', name: 'Regular Admission', enabled: true, capacity: 200, price: 35,
                    badgeText: 'REGULAR', badgeBg: '#e0f2fe', badgeColor: '#0369a1', cardBg: '#ffffff', cardBorderColor: '#0284c7', textColor: '#0f172a', buttonText: 'Get Regular Ticket', perks: 'Standard seating & event badge'
                },
                {
                    id: 'tier-vip', type: 'vip', name: 'VIP Access Pass', enabled: true, capacity: 25, price: 95,
                    badgeText: 'VIP PASS', badgeBg: '#f3e8ff', badgeColor: '#7e22ce', cardBg: '#faf5ff', cardBorderColor: '#a855f7', textColor: '#0f172a', buttonText: 'Get VIP Access', perks: 'Front-row reserved seating, VIP Lounge & Official Swag'
                }
            ]
        }
    ],
};

/**
 * Helper to check if the Event Ticketing & Pass add-on module is enabled and unlocked (paywall cleared).
 */
export const isTicketingUnlocked = (config = {}) => {
    const mod = config?.modules?.eventTicketing || DEFAULT_TICKETING_CONFIG;
    if (mod.enabled === false) {
        return false;
    }
    if (mod.isLocked === true && !mod.licenseKey) {
        return false;
    }
    return true;
};

export const EventTicketingManager = ({ config, onSave }) => {
    const modules = config?.modules || {};
    const ticketing = modules.eventTicketing || DEFAULT_TICKETING_CONFIG;

    const [activeTab, setActiveTab] = useState('events'); // 'events' | 'tickets' | 'integrations' | 'checkin'
    const [eventsList, setEventsList] = useState(ticketing.events || DEFAULT_TICKETING_CONFIG.events);
    const [issuedTickets, setIssuedTickets] = useState(ticketing.issuedTickets || []);

    // Integration Settings
    const [ebToken, setEbToken] = useState(ticketing.eventbriteToken || '');
    const [ebOrgId, setEbOrgId] = useState(ticketing.eventbriteOrgId || '');

    const [editingEvent, setEditingEvent] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [viewingTicket, setViewingTicket] = useState(null);

    const [scanInput, setScanInput] = useState('');
    const [scanResult, setScanResult] = useState(null);

    const updateTicketingModule = (newModuleData) => {
        const updated = {
            ...config,
            modules: {
                ...modules,
                eventTicketing: {
                    ...ticketing,
                    ...newModuleData
                }
            }
        };
        onSave(updated);
    };

    const handleSaveEvent = (evtToSave) => {
        let updatedEvents;
        if (eventsList.some(e => e.id === evtToSave.id)) {
            updatedEvents = eventsList.map(e => e.id === evtToSave.id ? evtToSave : e);
        } else {
            updatedEvents = [...eventsList, evtToSave];
        }
        setEventsList(updatedEvents);
        updateTicketingModule({ events: updatedEvents });
        setEditingEvent(null);
        setIsCreating(false);
    };

    const handleDeleteEvent = (id) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        const updatedEvents = eventsList.filter(e => e.id !== id);
        setEventsList(updatedEvents);
        updateTicketingModule({ events: updatedEvents });
    };

    const handleIssueManualTicket = (eventId, tierType, name, email, paidAmount) => {
        const evt = eventsList.find(e => e.id === eventId);
        if (!evt) return;
        const tier = evt.tiers?.find(t => t.type === tierType) || { name: tierType };

        const newTicket = {
            ticketId: `TCK-${Math.floor(100000 + Math.random() * 900000)}`,
            eventId,
            eventTitle: evt.title,
            eventDate: evt.date,
            eventTime: evt.time,
            eventLocation: evt.location,
            tierType,
            tierName: tier.name || tierType.toUpperCase(),
            paidAmount: parseFloat(paidAmount || 0),
            attendeeName: name,
            attendeeEmail: email,
            issuedAt: new Date().toISOString(),
            status: 'valid',
            qrCodePayload: `OMNIVASO-TICKET:${eventId}:${tierType}:${name}:${email}:${Date.now()}`
        };

        const updatedList = [newTicket, ...issuedTickets];
        setIssuedTickets(updatedList);
        updateTicketingModule({ issuedTickets: updatedList });
        setViewingTicket(newTicket);
    };

    const handleVerifyTicketCode = (code) => {
        const cleanCode = (code || '').trim().toUpperCase();
        if (!cleanCode) return;

        const ticket = issuedTickets.find(t =>
            t.ticketId.toUpperCase() === cleanCode ||
            t.qrCodePayload === code ||
            (t.qrCodePayload && t.qrCodePayload.toUpperCase().includes(cleanCode))
        );

        if (!ticket) {
            setScanResult({ type: 'error', message: `Ticket code "${cleanCode}" not found!` });
            return;
        }

        if (ticket.status === 'checked_in') {
            setScanResult({
                type: 'warning',
                ticket,
                message: `ALREADY CHECKED IN at ${new Date(ticket.checkedInAt).toLocaleTimeString()}`
            });
            return;
        }

        if (ticket.status === 'cancelled') {
            setScanResult({ type: 'error', ticket, message: `CANCELLED TICKET!` });
            return;
        }

        const now = new Date().toISOString();
        const updatedList = issuedTickets.map(t =>
            t.ticketId === ticket.ticketId ? { ...t, status: 'checked_in', checkedInAt: now } : t
        );
        setIssuedTickets(updatedList);
        updateTicketingModule({ issuedTickets: updatedList });

        setScanResult({
            type: 'success',
            ticket: { ...ticket, status: 'checked_in', checkedInAt: now },
            message: `ADMIT GUEST! Ticket checked in.`
        });
    };

    return (
        <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: '#0f172a' }}>

            {/* Header Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LuTicket size={24} style={{ color: '#2563eb' }} /> Event Ticketing &amp; External Integrations Suite
                    </h3>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                        Native QR passes &amp; Authorized platform connections (Eventbrite, Ticketmaster, Humanitix)
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => setActiveTab('events')}
                        style={{
                            padding: '8px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer',
                            background: activeTab === 'events' ? '#2563eb' : '#f1f5f9', color: activeTab === 'events' ? '#fff' : '#475569',
                            display: 'inline-flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <LuCalendar size={15} /> Events &amp; Tiers ({eventsList.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('integrations')}
                        style={{
                            padding: '8px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer',
                            background: activeTab === 'integrations' ? '#e11d48' : '#f1f5f9', color: activeTab === 'integrations' ? '#fff' : '#475569',
                            display: 'inline-flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <LuLink size={15} /> Authorised Integrations (Eventbrite)
                    </button>
                    <button
                        onClick={() => setActiveTab('tickets')}
                        style={{
                            padding: '8px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer',
                            background: activeTab === 'tickets' ? '#2563eb' : '#f1f5f9', color: activeTab === 'tickets' ? '#fff' : '#475569',
                            display: 'inline-flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <LuTicket size={15} /> Issued Tickets ({issuedTickets.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('checkin')}
                        style={{
                            padding: '8px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer',
                            background: activeTab === 'checkin' ? '#059669' : '#f1f5f9', color: activeTab === 'checkin' ? '#fff' : '#475569',
                            display: 'inline-flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <LuScan size={15} /> Door Scanner
                    </button>
                </div>
            </div>

            {/* TAB 1: EVENTS & TIERS */}
            {activeTab === 'events' && (
                <div>
                    {!editingEvent && !isCreating ? (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>Active &amp; Upcoming Events</h4>
                                <button
                                    onClick={() => {
                                        setEditingEvent({
                                            id: `evt-${Date.now()}`,
                                            title: 'New Event',
                                            subtitle: '',
                                            date: new Date().toISOString().split('T')[0],
                                            time: '19:00',
                                            location: 'Venue Location or Online Stream',
                                            description: '',
                                            bannerUrl: '',
                                            provider: 'internal',
                                            externalTicketUrl: '',
                                            eventbriteEventId: '',
                                            externalButtonText: 'Get Tickets on Eventbrite',
                                            tiers: DEFAULT_TICKETING_CONFIG.events[0].tiers
                                        });
                                        setIsCreating(true);
                                    }}
                                    style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                >
                                    <LuPlus size={16} /> Create New Event
                                </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
                                {eventsList.map(evt => (
                                    <div key={evt.id} style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                        {evt.bannerUrl && (
                                            <div style={{ height: '140px', overflow: 'hidden', background: '#f8fafc' }}>
                                                <img src={evt.bannerUrl} alt={evt.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                        <div style={{ padding: '20px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <LuCalendar size={13} /> {evt.date} &bull; <LuClock size={13} /> {evt.time}
                                                </div>
                                                <span style={{
                                                    fontSize: '0.7rem', fontWeight: 800, padding: '2px 8px', borderRadius: '10px',
                                                    background: evt.provider === 'eventbrite' ? '#ffedd5' : evt.provider === 'ticketmaster' ? '#dbeafe' : evt.provider === 'humanitix' ? '#e0e7ff' : '#dcfce7',
                                                    color: evt.provider === 'eventbrite' ? '#c2410c' : evt.provider === 'ticketmaster' ? '#1d4ed8' : evt.provider === 'humanitix' ? '#4338ca' : '#15803d'
                                                }}>
                                                    {evt.provider === 'eventbrite' ? 'Eventbrite API' : evt.provider === 'ticketmaster' ? 'Ticketmaster' : evt.provider === 'humanitix' ? 'Humanitix' : 'OmniVaso Native'}
                                                </span>
                                            </div>

                                            <h4 style={{ margin: '4px 0 4px 0', fontSize: '1.2rem', color: '#0f172a' }}>{evt.title}</h4>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <LuMapPin size={13} /> {evt.location}
                                            </div>

                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                                                {evt.tiers?.map(t => (
                                                    <span key={t.id || t.type} style={{
                                                        fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: '12px',
                                                        background: t.badgeBg || '#e2e8f0', color: t.badgeColor || '#334155'
                                                    }}>
                                                        {t.badgeText || t.name}
                                                    </span>
                                                ))}
                                            </div>

                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => setEditingEvent(evt)}
                                                    style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', padding: '8px', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                                >
                                                    <LuPencil size={14} /> Edit Event &amp; Platform Link
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteEvent(evt.id)}
                                                    style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '8px 12px', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    <LuTrash2 size={15} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <EventEditorForm
                            eventData={editingEvent}
                            onSave={handleSaveEvent}
                            onCancel={() => { setEditingEvent(null); setIsCreating(false); }}
                        />
                    )}
                </div>
            )}

            {/* TAB 2: AUTHORISED INTEGRATIONS (EVENTBRITE, TICKETMASTER, ETC) */}
            {activeTab === 'integrations' && (
                <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '16px', padding: '28px', maxWidth: '850px', margin: '0 auto' }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.4rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LuLink size={22} style={{ color: '#2563eb' }} /> Authorised External Ticketing Connectors
                    </h3>
                    <p style={{ margin: '0 0 24px 0', fontSize: '0.9rem', color: '#64748b' }}>
                        Connect your official Eventbrite, Ticketmaster, or Humanitix accounts for unified event promotion, checkout redirects, and automatic widget embeds.
                    </p>

                    {/* 1. EVENTBRITE OFFICIAL CONNECTOR */}
                    <div style={{ background: '#fff7ed', border: '2px solid #fdba74', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <div style={{ background: '#f97316', color: '#fff', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1.2rem' }}>
                                eb
                            </div>
                            <div>
                                <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#9a3412' }}>Eventbrite API &amp; Embedded Checkout Connector</h4>
                                <div style={{ fontSize: '0.8rem', color: '#c2410c' }}>Embed Eventbrite widgets or sync checkout seamlessly on your site</div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#9a3412', display: 'block', marginBottom: '4px' }}>Eventbrite Private API Token</label>
                                <input
                                    type="password"
                                    value={ebToken}
                                    onChange={(e) => setEbToken(e.target.value)}
                                    placeholder="e.g. EB_API_TOKEN_XXXXX"
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #fdba74', background: '#fff' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#9a3412', display: 'block', marginBottom: '4px' }}>Eventbrite Organization ID</label>
                                <input
                                    type="text"
                                    value={ebOrgId}
                                    onChange={(e) => setEbOrgId(e.target.value)}
                                    placeholder="e.g. 123456789012"
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #fdba74', background: '#fff' }}
                                />
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                updateTicketingModule({ eventbriteToken: ebToken, eventbriteOrgId: ebOrgId });
                                alert('Eventbrite API credentials saved!');
                            }}
                            style={{ background: '#ea580c', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        >
                            <LuSave size={15} /> Save Eventbrite Credentials
                        </button>
                    </div>

                    {/* 2. OTHER AUTHORISED PLATFORMS */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ fontWeight: 800, color: '#1e40af', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <LuTicket size={16} /> Ticketmaster / LiveNation
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#3b82f6', margin: '0 0 10px 0' }}>Paste direct Ticketmaster checkout URLs into any event configuration.</p>
                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#1d4ed8', background: '#dbeafe', padding: '2px 8px', borderRadius: '6px' }}>Active &bull; Deep Link Support</span>
                        </div>

                        <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ fontWeight: 800, color: '#5b21b6', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <LuTicket size={16} /> Humanitix Ticketing
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#7c3aed', margin: '0 0 10px 0' }}>Non-profit &amp; ethical event ticketing redirection support.</p>
                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#6d28d9', background: '#ede9fe', padding: '2px 8px', borderRadius: '6px' }}>Active &bull; Deep Link Support</span>
                        </div>

                        <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ fontWeight: 800, color: '#334155', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <LuGlobe size={16} /> Custom Ticketing URL
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 10px 0' }}>Link out to any authorized ticketing provider or booking platform.</p>
                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#475569', background: '#e2e8f0', padding: '2px 8px', borderRadius: '6px' }}>Active &bull; Any Platform</span>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: ISSUED TICKETS ROSTER */}
            {activeTab === 'tickets' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem' }}>All Registered Guest Tickets</h4>
                        <button
                            onClick={() => {
                                const name = prompt('Attendee Full Name:');
                                if (!name) return;
                                const email = prompt('Attendee Email:');
                                const evtId = eventsList[0]?.id;
                                if (!evtId) { alert('Create an event first!'); return; }
                                handleIssueManualTicket(evtId, 'early_bird', name, email, 18);
                            }}
                            style={{ background: '#059669', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                        >
                            + Issue Ticket Manually
                        </button>
                    </div>

                    {issuedTickets.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #cbd5e1' }}>
                            <div style={{ marginBottom: '8px' }}><LuTicket size={36} style={{ color: '#94a3b8' }} /></div>
                            <h4 style={{ margin: 0, color: '#64748b' }}>No tickets issued yet.</h4>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                                        <th style={{ padding: '12px 16px' }}>Ticket ID</th>
                                        <th style={{ padding: '12px 16px' }}>Attendee</th>
                                        <th style={{ padding: '12px 16px' }}>Event</th>
                                        <th style={{ padding: '12px 16px' }}>Tier</th>
                                        <th style={{ padding: '12px 16px' }}>Paid</th>
                                        <th style={{ padding: '12px 16px' }}>Status</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {issuedTickets.map(t => (
                                        <tr key={t.ticketId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: 700, fontFamily: 'monospace' }}>{t.ticketId}</td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <div style={{ fontWeight: 700, color: '#0f172a' }}>{t.attendeeName}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t.attendeeEmail}</div>
                                            </td>
                                            <td style={{ padding: '12px 16px', color: '#334155' }}>{t.eventTitle}</td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span style={{
                                                    padding: '3px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700,
                                                    background: t.tierType === 'early_bird' ? '#fef3c7' : t.tierType === 'free' ? '#dcfce7' : t.tierType === 'vip' ? '#f3e8ff' : '#e0f2fe',
                                                    color: t.tierType === 'early_bird' ? '#b45309' : t.tierType === 'free' ? '#15803d' : t.tierType === 'vip' ? '#7e22ce' : '#0369a1'
                                                }}>
                                                    {t.tierName}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 16px', fontWeight: 700 }}>
                                                {t.paidAmount === 0 ? 'FREE' : `$${t.paidAmount.toFixed(2)}`}
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span style={{
                                                    padding: '3px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700,
                                                    background: t.status === 'checked_in' ? '#dcfce7' : '#e0f2fe',
                                                    color: t.status === 'checked_in' ? '#166534' : '#0369a1',
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px'
                                                }}>
                                                    {t.status === 'checked_in' ? <LuCheck size={12} /> : <LuTicket size={12} />}
                                                    {t.status === 'checked_in' ? 'Checked In' : 'Valid'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                <button
                                                    onClick={() => setViewingTicket(t)}
                                                    style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                                >
                                                    <span>View / Print QR</span>
                                                    <LuPrinter size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 4: CHECK-IN */}
            {activeTab === 'checkin' && (
                <div style={{ maxWidth: '600px', margin: '0 auto', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '16px', padding: '28px', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <div style={{ marginBottom: '8px' }}><LuScan size={40} style={{ color: '#2563eb' }} /></div>
                        <h3 style={{ margin: 0, fontSize: '1.4rem', color: '#0f172a' }}>Door Ticket Check-In</h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>Scan QR Code or enter Ticket ID to validate pass</p>
                    </div>

                    <form onSubmit={(e) => { e.preventDefault(); handleVerifyTicketCode(scanInput); setScanInput(''); }}>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                            <input
                                type="text"
                                value={scanInput}
                                onChange={(e) => setScanInput(e.target.value)}
                                placeholder="Scan QR or enter Ticket ID (e.g. TCK-98234-A7)"
                                autoFocus
                                style={{ flex: 1, padding: '14px 16px', borderRadius: '10px', border: '2px solid #2563eb', fontSize: '1.05rem', fontWeight: 700, outline: 'none' }}
                            />
                            <button
                                type="submit"
                                style={{ background: '#059669', color: '#fff', border: 'none', padding: '0 24px', borderRadius: '10px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}
                            >
                                Validate
                            </button>
                        </div>
                    </form>

                    {scanResult && (
                        <div style={{
                            padding: '20px', borderRadius: '12px', marginTop: '16px',
                            background: scanResult.type === 'success' ? '#f0fdf4' : scanResult.type === 'warning' ? '#fffbeb' : '#fef2f2',
                            border: `2px solid ${scanResult.type === 'success' ? '#22c55e' : scanResult.type === 'warning' ? '#f59e0b' : '#ef4444'}`,
                        }}>
                            <h4 style={{ margin: 0, fontSize: '1.2rem', color: scanResult.type === 'success' ? '#15803d' : scanResult.type === 'warning' ? '#b45309' : '#b91c1c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {scanResult.type === 'success' ? <LuCheck size={20} /> : <LuCircleAlert size={20} />}
                                {scanResult.message}
                            </h4>

                            {scanResult.ticket && (
                                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.1)', fontSize: '0.9rem', color: '#1e293b' }}>
                                    <div><strong>Guest:</strong> {scanResult.ticket.attendeeName} ({scanResult.ticket.attendeeEmail})</div>
                                    <div><strong>Ticket ID:</strong> <code>{scanResult.ticket.ticketId}</code></div>
                                    <div><strong>Pass Tier:</strong> {scanResult.ticket.tierName}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* PRINTABLE TICKET MODAL */}
            {viewingTicket && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '20px' }}>
                    <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '480px', padding: '32px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', position: 'relative' }}>
                        <button
                            onClick={() => setViewingTicket(null)}
                            style={{ position: 'absolute', top: '16px', right: '16px', background: '#f1f5f9', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}
                        >
                            <LuX size={16} />
                        </button>

                        <div style={{ textAlign: 'center', borderBottom: '2px dashed #e2e8f0', paddingBottom: '20px', marginBottom: '20px' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '1px' }}>OFFICIAL ADMISSION PASS</div>
                            <h3 style={{ margin: '6px 0 2px 0', fontSize: '1.3rem', color: '#0f172a' }}>{viewingTicket.eventTitle}</h3>
                            <div style={{ fontSize: '0.85rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><LuCalendar size={14} /> {viewingTicket.eventDate}</span>
                                <span>&bull;</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><LuClock size={14} /> {viewingTicket.eventTime}</span>
                            </div>
                        </div>

                        <div style={{ textAlign: 'center', background: '#f8fafc', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                            <div
                                dangerouslySetInnerHTML={{ __html: generateQRCodeSVG(viewingTicket.qrCodePayload || viewingTicket.ticketId, { size: 180 }) }}
                                style={{ display: 'inline-block' }}
                            />
                            <div style={{ fontSize: '0.9rem', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '1px', marginTop: '10px', color: '#0f172a' }}>
                                {viewingTicket.ticketId}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.85rem', marginBottom: '20px' }}>
                            <div>
                                <div style={{ color: '#64748b', fontSize: '0.75rem' }}>ATTENDEE</div>
                                <div style={{ fontWeight: 700, color: '#0f172a' }}>{viewingTicket.attendeeName}</div>
                            </div>
                            <div>
                                <div style={{ color: '#64748b', fontSize: '0.75rem' }}>TIER &amp; PRICE</div>
                                <div style={{ fontWeight: 700, color: '#0f172a' }}>
                                    {viewingTicket.tierName} ({viewingTicket.paidAmount === 0 ? 'FREE' : `$${viewingTicket.paidAmount.toFixed(2)}`})
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => window.print()}
                            style={{ width: '100%', background: '#0f172a', color: '#fff', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        >
                            <LuPrinter size={16} />
                            <span>Print Ticket / Save PDF</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Event Editor Form
const EventEditorForm = ({ eventData, onSave, onCancel }) => {
    const [form, setForm] = useState(eventData);

    const handleTierChange = (index, key, val) => {
        const updatedTiers = [...form.tiers];
        updatedTiers[index] = { ...updatedTiers[index], [key]: val };
        setForm({ ...form, tiers: updatedTiers });
    };

    const handleAddCustomTier = () => {
        const newTier = {
            id: `tier-custom-${Date.now()}`,
            type: 'custom',
            name: 'Custom Ticket Tier',
            enabled: true,
            capacity: 100,
            price: 25,
            badgeText: 'SPECIAL PASS',
            badgeBg: '#e0e7ff',
            badgeColor: '#3730a3',
            cardBg: '#ffffff',
            cardBorderColor: '#6366f1',
            textColor: '#0f172a',
            buttonText: 'Reserve Pass',
            perks: 'Custom event perks'
        };
        setForm({ ...form, tiers: [...(form.tiers || []), newTier] });
    };

    const handleRemoveTier = (idx) => {
        const updated = form.tiers.filter((_, i) => i !== idx);
        setForm({ ...form, tiers: updated });
    };

    return (
        <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '16px', padding: '28px', maxWidth: '900px', margin: '0 auto' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.3rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LuSettings size={20} />
                Event &amp; Ticketing Platform Integration
            </h3>

            {/* Provider & Integration Option Selection */}
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <LuLink size={14} />
                    Select Authorised Ticketing Engine / Platform:
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                    {[
                        { id: 'internal', label: 'OmniVaso Native Passes', desc: 'In-house QR code generation & door scanner' },
                        { id: 'eventbrite', label: 'Eventbrite Official', desc: 'Direct Eventbrite checkout & API widgets' },
                        { id: 'ticketmaster', label: 'Ticketmaster / LiveNation', desc: 'Deep link to Ticketmaster event page' },
                        { id: 'humanitix', label: 'Humanitix Platform', desc: 'Ethical ticketing checkout links' },
                        { id: 'custom', label: 'Custom Ticketing URL', desc: 'Link to any external ticketing site' }
                    ].map(p => (
                        <div
                            key={p.id}
                            onClick={() => setForm({ ...form, provider: p.id })}
                            style={{
                                padding: '12px', borderRadius: '10px', cursor: 'pointer',
                                border: `2px solid ${form.provider === p.id ? '#2563eb' : '#cbd5e1'}`,
                                background: form.provider === p.id ? '#eff6ff' : '#fff'
                            }}
                        >
                            <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#0f172a' }}>{p.label}</div>
                            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>{p.desc}</div>
                        </div>
                    ))}
                </div>

                {/* External Provider Fields */}
                {form.provider !== 'internal' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', paddingTop: '12px', borderTop: '1px dashed #cbd5e1' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Authorised External Event URL *</label>
                            <input
                                type="text"
                                value={form.externalTicketUrl || ''}
                                onChange={(e) => setForm({ ...form, externalTicketUrl: e.target.value })}
                                placeholder={form.provider === 'eventbrite' ? 'https://www.eventbrite.com/e/my-event-12345678' : 'https://...'}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>

                        {form.provider === 'eventbrite' && (
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Eventbrite Event ID (For Embedded Widget)</label>
                                <input
                                    type="text"
                                    value={form.eventbriteEventId || ''}
                                    onChange={(e) => setForm({ ...form, eventbriteEventId: e.target.value })}
                                    placeholder="e.g. 1234567890"
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                />
                            </div>
                        )}

                        <div>
                            <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Button CTA Label</label>
                            <input
                                type="text"
                                value={form.externalButtonText || 'Buy Tickets on Eventbrite'}
                                onChange={(e) => setForm({ ...form, externalButtonText: e.target.value })}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Event Title</label>
                    <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    />
                </div>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Banner Image URL</label>
                    <input
                        type="text"
                        value={form.bannerUrl || ''}
                        onChange={(e) => setForm({ ...form, bannerUrl: e.target.value })}
                        placeholder="https://..."
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    />
                </div>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Event Date</label>
                    <input
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    />
                </div>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Event Time</label>
                    <input
                        type="time"
                        value={form.time}
                        onChange={(e) => setForm({ ...form, time: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                    />
                </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Venue Location or Stream Link</label>
                <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
            </div>

            {/* Ticket Tiers Editor */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '28px 0 14px 0' }}>
                <h4 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LuTicket size={18} />
                    Configured Ticket Tiers &amp; Styling
                </h4>
                <button
                    type="button"
                    onClick={handleAddCustomTier}
                    style={{ background: '#059669', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                    <LuPlus size={14} />
                    <span>Add Ticket Tier</span>
                </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
                {form.tiers?.map((tier, idx) => (
                    <div key={tier.id || idx} style={{
                        padding: '20px', borderRadius: '14px', border: `2px solid ${tier.cardBorderColor || '#e2e8f0'}`,
                        background: tier.cardBg || '#ffffff', color: tier.textColor || '#0f172a'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{
                                    fontSize: '0.75rem', fontWeight: 800, padding: '3px 10px', borderRadius: '12px',
                                    background: tier.badgeBg || '#e2e8f0', color: tier.badgeColor || '#334155'
                                }}>
                                    {tier.badgeText || tier.type.toUpperCase()}
                                </span>
                                <strong style={{ fontSize: '1rem' }}>{tier.name}</strong>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{ fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input
                                        type="checkbox"
                                        checked={tier.enabled !== false}
                                        onChange={(e) => handleTierChange(idx, 'enabled', e.target.checked)}
                                    /> Enabled
                                </label>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveTier(idx)}
                                    style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <LuTrash2 size={14} />
                                </button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Ticket Name (Editable)</label>
                                <input
                                    type="text"
                                    value={tier.name}
                                    onChange={(e) => handleTierChange(idx, 'name', e.target.value)}
                                    style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Tier Type</label>
                                <select
                                    value={tier.type}
                                    onChange={(e) => handleTierChange(idx, 'type', e.target.value)}
                                    style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                >
                                    <option value="early_bird">Early Bird</option>
                                    <option value="free">Free</option>
                                    <option value="sliding_scale">Sliding Scale</option>
                                    <option value="regular">Regular</option>
                                    <option value="vip">VIP Pass</option>
                                    <option value="custom">Custom Tier</option>
                                </select>
                            </div>

                            {tier.type === 'sliding_scale' ? (
                                <>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Min Price ($)</label>
                                        <input
                                            type="number"
                                            value={tier.minPrice || 5}
                                            onChange={(e) => handleTierChange(idx, 'minPrice', parseFloat(e.target.value))}
                                            style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Max Price ($)</label>
                                        <input
                                            type="number"
                                            value={tier.maxPrice || 50}
                                            onChange={(e) => handleTierChange(idx, 'maxPrice', parseFloat(e.target.value))}
                                            style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                        />
                                    </div>
                                </>
                            ) : tier.type !== 'free' ? (
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Price ($)</label>
                                    <input
                                        type="number"
                                        value={tier.price || 0}
                                        onChange={(e) => handleTierChange(idx, 'price', parseFloat(e.target.value))}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                            ) : null}

                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Badge Label</label>
                                <input
                                    type="text"
                                    value={tier.badgeText || ''}
                                    onChange={(e) => handleTierChange(idx, 'badgeText', e.target.value)}
                                    placeholder="e.g. EARLY BIRD"
                                    style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Card Background Color</label>
                                <input
                                    type="color"
                                    value={tier.cardBg || '#ffffff'}
                                    onChange={(e) => handleTierChange(idx, 'cardBg', e.target.value)}
                                    style={{ width: '100%', height: '34px', padding: '2px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Border Accent Color</label>
                                <input
                                    type="color"
                                    value={tier.cardBorderColor || '#e2e8f0'}
                                    onChange={(e) => handleTierChange(idx, 'cardBorderColor', e.target.value)}
                                    style={{ width: '100%', height: '34px', padding: '2px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: '10px' }}>
                            <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Perks &amp; Benefits Description</label>
                            <input
                                type="text"
                                value={tier.perks || ''}
                                onChange={(e) => handleTierChange(idx, 'perks', e.target.value)}
                                placeholder="e.g. Includes early venue access, complimentary beverage"
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                    onClick={onCancel}
                    style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                    Cancel
                </button>
                <button
                    onClick={() => onSave(form)}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                    Save Event &amp; External Integration
                </button>
            </div>
        </div>
    );
};
